/**
 * Simultaneous movement resolution.
 *
 *   [Collision Type #1] two players may not occupy one cell. Directions resolve
 *   simultaneously; on a conflict neither moves and a 💥 renders on the cell.
 *   [Collision Type #2] walking into a wall or a station turns the player to
 *   face it instead. That is how a player aims at a station.
 *
 * Conflicts produce an event rather than silence, and the event goes to every
 * client in the room. That is the fix for the Craft Together failure mode: the
 * problem was never that resolution was slow, it was that it was invisible to
 * the winner and private to the loser.
 */

import type { Cell, Direction, GameEvent, PlayerId, PlayerInput } from '@overcooked/shared'
import { cellAhead, cellEquals, cellKey } from '@overcooked/shared'
import type { GameState } from './state'
import { isWalkable, playersBySeat } from './state'

interface Intent {
  playerId: PlayerId
  seat: number
  from: Cell
  direction: Direction
  to: Cell
}

function emitTurn(state: GameState, playerId: PlayerId, direction: Direction, events: GameEvent[]): void {
  const player = state.players[playerId]
  if (player.facing === direction) return
  player.facing = direction
  events.push({ tick: state.tick, wallClock: 0, actor: playerId, type: 'PLAYER_TURNED', playerId, facing: direction })
}

/**
 * One move per player per tick. A client that key-repeats faster than 50 Hz has
 * its extra inputs dropped rather than queued, so a held key cannot bank moves.
 */
function collectIntents(state: GameState, inputs: PlayerInput[]): Intent[] {
  const seen = new Set<PlayerId>()
  const intents: Intent[] = []

  for (const input of inputs) {
    if (input.action.kind !== 'move') continue
    const player = state.players[input.playerId]
    if (!player || !player.connected) continue
    if (seen.has(input.playerId)) continue
    seen.add(input.playerId)

    intents.push({
      playerId: player.id,
      seat: player.seat,
      from: { ...player.cell },
      direction: input.action.direction,
      to: cellAhead(player.cell, input.action.direction),
    })
  }

  return intents.sort((a, b) => a.seat - b.seat)
}

export function resolveMovement(state: GameState, inputs: PlayerInput[], events: GameEvent[]): void {
  let movers = collectIntents(state, inputs)
  if (movers.length === 0) return

  // Collision Type #2 — terrain. Turn to aim, do not move.
  const blocked: Intent[] = []
  movers = movers.filter((intent) => {
    if (isWalkable(state, intent.to)) return true
    blocked.push(intent)
    return false
  })
  for (const intent of blocked) {
    emitTurn(state, intent.playerId, intent.direction, events)
    events.push({
      tick: state.tick,
      wallClock: 0,
      actor: intent.playerId,
      type: 'PLAYER_BLOCKED',
      playerId: intent.playerId,
      cell: intent.to,
    })
  }

  // Collision Type #1a — two or more players want the same cell this tick.
  const byTarget = new Map<string, Intent[]>()
  for (const intent of movers) {
    const key = cellKey(intent.to)
    const group = byTarget.get(key)
    if (group) group.push(intent)
    else byTarget.set(key, [intent])
  }

  const contested = new Set<PlayerId>()
  for (const [, group] of byTarget) {
    if (group.length < 2) continue
    for (const intent of group) {
      contested.add(intent.playerId)
      emitTurn(state, intent.playerId, intent.direction, events)
    }
    events.push({
      tick: state.tick,
      wallClock: 0,
      actor: 'system',
      type: 'COLLISION',
      cell: group[0].to,
      playerIds: group.map((i) => i.playerId),
    })
  }
  movers = movers.filter((intent) => !contested.has(intent.playerId))

  // Collision Type #1b — a direct swap. Neither may pass through the other.
  const swapped = new Set<PlayerId>()
  for (let i = 0; i < movers.length; i += 1) {
    for (let j = i + 1; j < movers.length; j += 1) {
      const a = movers[i]
      const b = movers[j]
      if (swapped.has(a.playerId) || swapped.has(b.playerId)) continue
      if (!cellEquals(a.to, b.from) || !cellEquals(b.to, a.from)) continue

      swapped.add(a.playerId)
      swapped.add(b.playerId)
      emitTurn(state, a.playerId, a.direction, events)
      emitTurn(state, b.playerId, b.direction, events)
      events.push({
        tick: state.tick,
        wallClock: 0,
        actor: 'system',
        type: 'COLLISION',
        cell: a.to,
        playerIds: [a.playerId, b.playerId],
      })
    }
  }
  movers = movers.filter((intent) => !swapped.has(intent.playerId))

  // A mover heading into a cell held by somebody who is not vacating it this
  // tick is blocked. Run to a fixpoint so a chain (A behind B behind a wall)
  // resolves in one tick rather than one player per tick.
  const stationary = new Map<string, PlayerId>()
  const movingIds = new Set(movers.map((m) => m.playerId))
  for (const player of playersBySeat(state)) {
    if (!movingIds.has(player.id)) stationary.set(cellKey(player.cell), player.id)
  }

  let settled = false
  while (!settled) {
    settled = true
    for (const intent of [...movers]) {
      if (!stationary.has(cellKey(intent.to))) continue
      movers = movers.filter((m) => m.playerId !== intent.playerId)
      stationary.set(cellKey(intent.from), intent.playerId)
      emitTurn(state, intent.playerId, intent.direction, events)
      events.push({
        tick: state.tick,
        wallClock: 0,
        actor: intent.playerId,
        type: 'PLAYER_BLOCKED',
        playerId: intent.playerId,
        cell: intent.to,
      })
      settled = false
    }
  }

  for (const intent of movers) {
    const player = state.players[intent.playerId]
    player.cell = { ...intent.to }
    player.facing = intent.direction
    events.push({
      tick: state.tick,
      wallClock: 0,
      actor: intent.playerId,
      type: 'PLAYER_MOVED',
      playerId: intent.playerId,
      to: { ...intent.to },
      facing: intent.direction,
    })
  }
}
